import { ChangeEvent } from 'react';
import styled from 'styled-components';

import { Input } from '../../../../../../../components/atoms/input/input';
import { ClienteTaxa } from '../../../../../../../types/cliente-taxa';
import { TaxaFieldErrors } from './validation';

interface Props {
  values: ClienteTaxa;
  errors: TaxaFieldErrors;
  onChange: (field: keyof ClienteTaxa, value: number | '') => void;
}

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.bgSurface};

  & + & {
    margin-top: 12px;
  }
`;

const GroupTitle = styled.h4`
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FieldError = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.danger};
`;

interface NumberFieldProps {
  label: string;
  field: keyof ClienteTaxa;
  value: number;
  step?: string;
  error?: string;
  testId: string;
  onChange: (field: keyof ClienteTaxa, value: number | '') => void;
}

const NumberField = ({
  label,
  field,
  value,
  step,
  error,
  testId,
  onChange,
}: NumberFieldProps) => {
  const handle = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(field, '');
      return;
    }
    const parsed = Number(raw);
    onChange(field, Number.isFinite(parsed) ? parsed : '');
  };

  return (
    <Field>
      <FieldLabel htmlFor={testId}>{label}</FieldLabel>
      <Input
        id={testId}
        data-testid={testId}
        type="number"
        step={step ?? '0.01'}
        min={0}
        value={Number.isFinite(value) ? value : ''}
        onChange={handle}
        hasError={Boolean(error)}
      />
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
};

export const TaxaFormTab = ({ values, errors, onChange }: Props) => (
  <>
    <Group>
      <GroupTitle>Geral</GroupTitle>
      <Grid>
        <NumberField
          label="Tempo de recebimento (dias)"
          field="tempoRecebimento"
          step="1"
          value={values.tempoRecebimento}
          error={errors.tempoRecebimento}
          testId={`taxa-${values.recebimento}-tempoRecebimento`}
          onChange={onChange}
        />
        <NumberField
          label="Taxa de saque %"
          field="taxaSaque"
          value={values.taxaSaque}
          error={errors.taxaSaque}
          testId={`taxa-${values.recebimento}-taxaSaque`}
          onChange={onChange}
        />
      </Grid>
    </Group>

    <Group>
      <GroupTitle>Nacional</GroupTitle>
      <Grid>
        <NumberField
          label="Taxa nacional %"
          field="taxaNacional"
          value={values.taxaNacional}
          error={errors.taxaNacional}
          testId={`taxa-${values.recebimento}-taxaNacional`}
          onChange={onChange}
        />
        <NumberField
          label="Taxa PIX %"
          field="taxaPix"
          value={values.taxaPix}
          error={errors.taxaPix}
          testId={`taxa-${values.recebimento}-taxaPix`}
          onChange={onChange}
        />
        <NumberField
          label="Reserva nacional %"
          field="reservaNacional"
          value={values.reservaNacional}
          error={errors.reservaNacional}
          testId={`taxa-${values.recebimento}-reservaNacional`}
          onChange={onChange}
        />
        <NumberField
          label="Taxa extra nacional %"
          field="taxaExtraNacional"
          value={values.taxaExtraNacional}
          error={errors.taxaExtraNacional}
          testId={`taxa-${values.recebimento}-taxaExtraNacional`}
          onChange={onChange}
        />
        <NumberField
          label="Tempo reserva nacional (dias)"
          field="tempoReservaNacional"
          step="1"
          value={values.tempoReservaNacional}
          error={errors.tempoReservaNacional}
          testId={`taxa-${values.recebimento}-tempoReservaNacional`}
          onChange={onChange}
        />
      </Grid>
    </Group>

    <Group>
      <GroupTitle>Internacional</GroupTitle>
      <Grid>
        <NumberField
          label="Taxa internacional %"
          field="taxaInternacional"
          value={values.taxaInternacional}
          error={errors.taxaInternacional}
          testId={`taxa-${values.recebimento}-taxaInternacional`}
          onChange={onChange}
        />
        <NumberField
          label="Reserva global %"
          field="reservaGlobal"
          value={values.reservaGlobal}
          error={errors.reservaGlobal}
          testId={`taxa-${values.recebimento}-reservaGlobal`}
          onChange={onChange}
        />
        <NumberField
          label="Taxa extra internacional %"
          field="taxaExtraInternacional"
          value={values.taxaExtraInternacional}
          error={errors.taxaExtraInternacional}
          testId={`taxa-${values.recebimento}-taxaExtraInternacional`}
          onChange={onChange}
        />
        <NumberField
          label="Tempo reserva global (dias)"
          field="tempoReservaGlobal"
          step="1"
          value={values.tempoReservaGlobal}
          error={errors.tempoReservaGlobal}
          testId={`taxa-${values.recebimento}-tempoReservaGlobal`}
          onChange={onChange}
        />
      </Grid>
    </Group>
  </>
);
